import {
  Activity,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Gauge,
  RefreshCw,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useMemo, type ComponentType, type SVGProps } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  useAdminStatsGetAdminStatsClientsLeaderboard,
  useAdminStatsGetAdminStatsOverview,
  useAdminStatsGetAdminStatsRequestsTimeseries,
} from '@/lib/api/admin-stats/admin-stats';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CountUp } from '@/components/CountUp';
import { useAuth } from '@/lib/auth/useAuth';
import { cn } from '@/lib/utils';

type IconKind = ComponentType<SVGProps<SVGSVGElement>>;

export default function DashboardPage() {
  const { user } = useAuth();

  const overview = useAdminStatsGetAdminStatsOverview({ query: { refetchInterval: 10_000 } });
  const timeseries = useAdminStatsGetAdminStatsRequestsTimeseries(
    { hours: 24, bucket: 'hour' },
    { query: { refetchInterval: 30_000 } },
  );
  const leaderboard = useAdminStatsGetAdminStatsClientsLeaderboard(
    { hours: 24, limit: 5 },
    { query: { refetchInterval: 30_000 } },
  );

  const chartData = useMemo(() => {
    if (!timeseries.data) return [];
    return timeseries.data.buckets.map((b) => ({
      t: new Date(b.bucketStart).getTime(),
      label: new Date(b.bucketStart).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      success: Number(b.success),
      failure: Number(b.failure),
      total: Number(b.total),
    }));
  }, [timeseries.data]);

  const successPct = asNumber(overview.data?.successRate);
  const requestsTotal = asNumber(overview.data?.totalRequests24h);
  const avgLatency = asNumber(overview.data?.avgLatencyMs);
  const activeClients = asNumber(overview.data?.activeClients);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 5) return 'Running late';
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    if (h < 21) return 'Good evening';
    return 'Good night';
  }, []);

  const firstName = (user?.fullName || user?.email || 'there').split(/\s|[@.]/)[0];

  return (
    <section className="space-y-6">
      {/* ---- hero ---- */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{greeting},</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight">{firstName}.</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Here's how the proxy has been performing over the last 24 hours.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <RefreshCw className={cn('h-3.5 w-3.5', overview.isFetching && 'animate-spin')} />
          <span>{overview.isFetching ? 'Refreshing…' : 'Auto-refreshing every 10s'}</span>
        </div>
      </header>

      {overview.isError ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">Could not load stats</CardTitle>
            <CardDescription>{(overview.error as Error)?.message ?? 'Unknown error'}</CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <>
          {/* ---- KPI grid ---- */}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              icon={Activity}
              iconTone="primary"
              label="Total requests"
              sub="last 24 hours"
              numericValue={overview.isLoading ? null : requestsTotal}
              format={(n) => formatNumber(n)}
            />
            <KpiCard
              icon={CheckCircle2}
              iconTone={successPct >= 0.99 ? 'emerald' : successPct >= 0.95 ? 'amber' : 'red'}
              label="Success rate"
              sub="2xx / all"
              // success rate is a 0..1 fraction. Animate the percentage so it
              // counts up; format mirrors formatPercent.
              numericValue={overview.isLoading ? null : successPct}
              format={(n) => (n === 0 ? '—' : `${(n * 100).toFixed(1)}%`)}
            />
            <KpiCard
              icon={Gauge}
              iconTone={avgLatency < 500 ? 'emerald' : avgLatency < 1500 ? 'amber' : 'red'}
              label="Avg latency"
              sub="end-to-end"
              numericValue={overview.isLoading ? null : avgLatency}
              format={(n) => `${formatNumber(n)} ms`}
            />
            <KpiCard
              icon={Users}
              iconTone="primary"
              label="Active clients"
              sub="in allowlist"
              numericValue={overview.isLoading ? null : activeClients}
              format={(n) => formatNumber(n)}
            />
          </div>

          {/* ---- chart ---- */}
          <Card>
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  Request volume
                </CardTitle>
                <CardDescription>Hourly buckets, split by success and failure.</CardDescription>
              </div>
              <Link
                to="/transactions"
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary hover:bg-accent/50"
              >
                Browse transactions
                <ArrowRight className="h-3 w-3" />
              </Link>
            </CardHeader>
            <CardContent className="h-[260px]">
              {timeseries.isLoading ? (
                <div className="h-full animate-pulse rounded-md bg-muted/40" />
              ) : chartData.length === 0 ? (
                <EmptyChartMessage />
              ) : (
                <div className="h-full motion-safe:animate-in motion-safe:fade-in motion-safe:duration-150">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradSuccess" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(160 84% 45%)" stopOpacity={0.45} />
                        <stop offset="100%" stopColor="hsl(160 84% 45%)" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="gradFailure" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(0 84% 60%)" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="hsl(0 84% 60%)" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.4} vertical={false} />
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      axisLine={false}
                      minTickGap={32}
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      width={36}
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="success"
                      stackId="1"
                      stroke="hsl(160 84% 45%)"
                      fill="url(#gradSuccess)"
                      strokeWidth={1.5}
                    />
                    <Area
                      type="monotone"
                      dataKey="failure"
                      stackId="1"
                      stroke="hsl(0 84% 60%)"
                      fill="url(#gradFailure)"
                      strokeWidth={1.5}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ---- secondary row ---- */}
          <div className="grid gap-4 lg:grid-cols-5">
            <Card className="lg:col-span-3">
              <CardHeader>
                <CardTitle className="text-base">Top clients</CardTitle>
                <CardDescription>Busiest partners in the last 24 hours.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {leaderboard.isLoading ? (
                  <div className="space-y-2 p-6">
                    {Array.from({ length: 4 }, (_, i) => (
                      <div key={i} className="h-10 animate-pulse rounded-md bg-muted/40" />
                    ))}
                  </div>
                ) : !leaderboard.data || leaderboard.data.length === 0 ? (
                  <p className="p-6 text-sm text-muted-foreground">
                    No traffic in the last 24 hours.
                  </p>
                ) : (
                  <ul className="divide-y divide-border/60 text-sm">
                    {leaderboard.data.map((row, i) => {
                      const topTotal = Number(leaderboard.data![0].totalRequests) || 1;
                      const pct = (Number(row.totalRequests) / topTotal) * 100;
                      const successRate = Number(row.successRate);
                      return (
                        <li key={row.clientId} className="group flex items-center gap-3 px-6 py-3">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted font-mono text-[10px] font-semibold text-muted-foreground">
                            {i + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline justify-between gap-3">
                              <Link
                                to={`/clients/${row.clientId}`}
                                className="truncate font-medium text-foreground hover:underline"
                              >
                                {row.clientName}
                              </Link>
                              <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                                {formatNumber(Number(row.totalRequests))}
                              </span>
                            </div>
                            <div className="mt-1 flex items-center gap-3">
                              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                                <div
                                  className="h-full rounded-full bg-primary/70 transition-all"
                                  style={{ width: `${Math.max(4, pct)}%` }}
                                />
                              </div>
                              <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                                {formatPercent(successRate)} · {formatNumber(Number(row.avgLatencyMs))} ms
                              </span>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <CardTitle className="text-base">Top errors</CardTitle>
                </div>
                <CardDescription>Most frequent proxy error codes.</CardDescription>
              </CardHeader>
              <CardContent>
                {overview.isLoading ? (
                  <div className="h-20 animate-pulse rounded-md bg-muted/40" />
                ) : overview.data?.topErrors?.length ? (
                  <ul className="space-y-2 text-sm">
                    {overview.data.topErrors.map((err) => (
                      <li
                        key={err.errorCode}
                        className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-card/40 px-3 py-2"
                      >
                        <code className="truncate rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">
                          {err.errorCode}
                        </code>
                        <Badge variant="outline" className="shrink-0 font-mono tabular-nums">
                          {err.count}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-4 w-4" />
                    No errors in the last 24 hours.
                  </div>
                )}
              </CardContent>
              <div className="px-6 pb-4">
                <Button asChild variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs">
                  <Link to="/logs">
                    Open live logs
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </Button>
              </div>
            </Card>
          </div>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// KPI card
// ---------------------------------------------------------------------------

interface KpiCardProps {
  icon: IconKind;
  iconTone: 'primary' | 'emerald' | 'amber' | 'red';
  label: string;
  sub: string;
  numericValue: number | null;
  format: (n: number) => string;
}

function KpiCard({ icon: Icon, iconTone, label, sub, numericValue, format }: KpiCardProps) {
  const tone = {
    primary: 'bg-primary/10 text-primary',
    emerald: 'bg-emerald-500/10 text-emerald-500',
    amber: 'bg-amber-500/10 text-amber-500',
    red: 'bg-rose-500/10 text-rose-500',
  }[iconTone];

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div>
          <CardDescription className="text-xs font-medium uppercase tracking-wider">
            {label}
          </CardDescription>
          <div className="mt-3 text-3xl font-semibold tabular-nums tracking-tight">
            <CountUp value={numericValue} format={format} />
          </div>
        </div>
        <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', tone)}>
          <Icon className="h-5 w-5" />
        </span>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-xs text-muted-foreground">{sub}</p>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Chart bits
// ---------------------------------------------------------------------------

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; dataKey: string; color: string }>;
  label?: string;
}

function ChartTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const total = payload.reduce((s, p) => s + (typeof p.value === 'number' ? p.value : 0), 0);
  return (
    <div className="min-w-[140px] rounded-lg border border-border/70 bg-popover/95 px-3 py-2 text-xs shadow-lg backdrop-blur">
      <div className="mb-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-3 py-0.5">
          <span className="flex items-center gap-1.5 capitalize text-muted-foreground">
            <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
            {p.dataKey}
          </span>
          <span className="font-mono tabular-nums text-foreground">{formatNumber(p.value)}</span>
        </div>
      ))}
      <div className="mt-1 flex items-center justify-between gap-3 border-t border-border/60 pt-1 font-medium">
        <span className="text-muted-foreground">Total</span>
        <span className="font-mono tabular-nums">{formatNumber(total)}</span>
      </div>
    </div>
  );
}

function EmptyChartMessage() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground">
      <Activity className="h-6 w-6 opacity-50" />
      <span>No proxy traffic yet — try issuing a call from Scalar.</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}

function formatPercent(value: number): string {
  if (!Number.isFinite(value) || value === 0) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function asNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}
